import React, { useState } from 'react';
import { CheckCircleIcon, LockIcon, CloudIcon, PhoneIcon, RefreshIcon, AnalyticsIcon, TargetIcon } from '../common/icons';
import './PricingSection.css';

interface PricingTier {
  id: string;
  name: string;
  description: string;
  price: {
    monthly: number;
    annual: number;
  };
  features: string[];
  limitations?: string[];
  popular?: boolean;
  cta: string;
  badge?: string;
}

interface PricingSectionProps {
  onSelectPlan?: (planId: string, billing: 'monthly' | 'annual') => void;
  onContactSales?: () => void;
}

const PricingSection: React.FC<PricingSectionProps> = ({ onSelectPlan, onContactSales }) => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');

  const pricingTiers: PricingTier[] = [
    {
      id: 'starter',
      name: 'Starter',
      description: 'Perfect for small businesses getting started with warehouse optimization',
      price: {
        monthly: 299,
        annual: 2390 // 20% discount
      },
      features: [
        'Up to 5 warehouse locations',
        'Basic inventory optimization',
        'Standard reporting dashboard',
        'Email support',
        '1,000 optimization runs/month',
        'Basic demand forecasting',
        'Standard API access'
      ],
      limitations: [
        'Limited to 10,000 SKUs',
        'Basic analytics only'
      ],
      cta: 'Start Free Trial'
    },
    {
      id: 'professional',
      name: 'Professional',
      description: 'Advanced features for growing businesses with complex supply chains',
      price: {
        monthly: 799,
        annual: 6392 // 33% discount
      },
      features: [
        'Up to 25 warehouse locations',
        'Advanced AI optimization',
        'Real-time analytics dashboard',
        'Priority support + phone',
        'Unlimited optimization runs',
        'Advanced demand forecasting',
        'Full API access',
        'Custom integrations',
        'Multi-user collaboration',
        'Advanced reporting suite'
      ],
      popular: true,
      badge: 'Most Popular',
      cta: 'Start Free Trial'
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      description: 'Complete solution for large organizations with enterprise-grade requirements',
      price: {
        monthly: 2499,
        annual: 19992 // 33% discount
      },
      features: [
        'Unlimited warehouse locations',
        'Custom AI model training',
        'White-label dashboard',
        'Dedicated success manager',
        'Unlimited everything',
        'Predictive analytics suite',
        'Enterprise API + webhooks',
        'Custom integrations + SSO',
        'Advanced user management',
        'Custom reporting + exports',
        'SLA guarantees',
        'On-premise deployment option'
      ],
      cta: 'Contact Sales'
    }
  ];

  const handlePlanSelect = (planId: string) => {
    if (planId === 'enterprise') {
      onContactSales?.();
    } else {
      onSelectPlan?.(planId, billingCycle);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };

  const calculateSavings = (monthly: number, annual: number) => {
    const monthlyCost = monthly * 12;
    const savings = monthlyCost - annual;
    const percentage = Math.round((savings / monthlyCost) * 100);
    return { amount: savings, percentage };
  };

  return (
    <section className="pricing-section" aria-labelledby="pricing-heading">
      <div className="pricing-container">
        <div className="pricing-header">
          <h2 id="pricing-heading" className="pricing-title">
            Simple, Transparent Pricing
          </h2>
          <p className="pricing-subtitle">
            Choose the perfect plan for your business. All plans include a 14-day free trial.
          </p>
          
          <div className="billing-toggle">
            <span className={`billing-option ${billingCycle === 'monthly' ? 'active' : ''}`}>
              Monthly
            </span>
            <button
              className="toggle-switch"
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
              aria-label="Toggle billing cycle"
            >
              <div className={`toggle-slider ${billingCycle === 'annual' ? 'active' : ''}`} />
            </button>
            <span className={`billing-option ${billingCycle === 'annual' ? 'active' : ''}`}>
              Annual
              <span className="savings-badge">Save up to 33%</span>
            </span>
          </div>
        </div>

        <div className="pricing-grid">
          {pricingTiers.map((tier) => {
            const savings = calculateSavings(tier.price.monthly, tier.price.annual);
            const currentPrice = tier.price[billingCycle];
            
            return (
              <div
                key={tier.id}
                className={`pricing-card ${tier.popular ? 'popular' : ''}`}
              >
                {tier.badge && (
                  <div className="pricing-badge">{tier.badge}</div>
                )}
                
                <div className="pricing-card-header">
                  <h3 className="tier-name">{tier.name}</h3>
                  <p className="tier-description">{tier.description}</p>
                  
                  <div className="pricing-display">
                    <div className="price-main">
                      <span className="price-amount">{formatPrice(currentPrice)}</span>
                      <span className="price-period">/{billingCycle === 'monthly' ? 'month' : 'year'}</span>
                    </div>
                    
                    {billingCycle === 'annual' && savings.percentage > 0 && (
                      <div className="price-savings">
                        Save {formatPrice(savings.amount)} ({savings.percentage}% off)
                      </div>
                    )}
                    
                    {billingCycle === 'monthly' && (
                      <div className="price-annual-hint">
                        {formatPrice(tier.price.annual)}/year if paid annually
                      </div>
                    )}
                  </div>
                </div>

                <div className="pricing-card-body">
                  <ul className="features-list">
                    {tier.features.map((feature, index) => (
                      <li key={index} className="feature-item">
                        <CheckCircleIcon width={16} height={16} color="#10B981" className="feature-check" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  {tier.limitations && (
                    <div className="limitations">
                      <h4 className="limitations-title">Limitations:</h4>
                      <ul className="limitations-list">
                        {tier.limitations.map((limitation, index) => (
                          <li key={index} className="limitation-item">
                            {limitation}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="pricing-card-footer">
                  <button
                    className={`btn ${tier.popular ? 'btn-primary' : 'btn-outline'} btn-large btn-full`}
                    onClick={() => handlePlanSelect(tier.id)}
                    aria-label={`Select ${tier.name} plan`}
                  >
                    {tier.cta}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="pricing-features">
          <h3 className="features-title">All Plans Include:</h3>
          <div className="features-grid">
            <div className="feature-item">
              <LockIcon width={20} height={20} color="#007AFF" className="feature-icon" />
              <span>Enterprise-grade security</span>
            </div>
            <div className="feature-item">
              <CloudIcon width={20} height={20} color="#007AFF" className="feature-icon" />
              <span>Cloud-based infrastructure</span>
            </div>
            <div className="feature-item">
              <PhoneIcon width={20} height={20} color="#007AFF" className="feature-icon" />
              <span>Mobile-responsive interface</span>
            </div>
            <div className="feature-item">
              <RefreshIcon width={20} height={20} color="#007AFF" className="feature-icon" />
              <span>Automatic updates</span>
            </div>
            <div className="feature-item">
              <AnalyticsIcon width={20} height={20} color="#007AFF" className="feature-icon" />
              <span>Real-time monitoring</span>
            </div>
            <div className="feature-item">
              <TargetIcon width={20} height={20} color="#007AFF" className="feature-icon" />
              <span>99.9% uptime SLA</span>
            </div>
          </div>
        </div>

        <div className="pricing-faq">
          <h3 className="faq-title">Frequently Asked Questions</h3>
          <div className="faq-grid">
            <div className="faq-item">
              <h4 className="faq-question">Can I change plans anytime?</h4>
              <p className="faq-answer">
                Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.
              </p>
            </div>
            <div className="faq-item">
              <h4 className="faq-question">Is there a setup fee?</h4>
              <p className="faq-answer">
                No setup fees for Starter and Professional plans. Enterprise plans may include implementation services.
              </p>
            </div>
            <div className="faq-item">
              <h4 className="faq-question">What payment methods do you accept?</h4>
              <p className="faq-answer">
                We accept all major credit cards, ACH transfers, and wire transfers for enterprise customers.
              </p>
            </div>
            <div className="faq-item">
              <h4 className="faq-question">Do you offer custom pricing?</h4>
              <p className="faq-answer">
                Yes, we offer custom pricing for large enterprises with specific requirements. Contact our sales team.
              </p>
            </div>
          </div>
        </div>

        <div className="pricing-cta">
          <h3 className="cta-title">Ready to Get Started?</h3>
          <p className="cta-description">
            Join thousands of companies optimizing their supply chains with Optinode AI.
          </p>
          <div className="cta-actions">
            <button 
              className="btn btn-primary btn-large"
              onClick={() => handlePlanSelect('professional')}
            >
              Start Free Trial
            </button>
            <button 
              className="btn btn-secondary btn-large"
              onClick={onContactSales}
            >
              Talk to Sales
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;